/**
 * Extracts a display name from an inline user object with Cognito attributes.
 *
 * Replaces the former N+1 pattern where useOrganizationUserNames fetched
 * all org users separately. Now user data comes inline on each activity
 * via the Activity.user GraphQL relation.
 * @module utils/getUserDisplayName
 * @see SE-3780 WhatsNew N+1 user names
 */

/** Minimal user shape needed for display name resolution */
interface ActivityUser {
  readonly id: string;
  readonly title?: string | null;
  readonly cognitoUser?: {
    readonly Attributes?:
      | readonly {
          readonly Name: string;
          readonly Value: string;
        }[]
      | null;
    readonly UserAttributes?:
      | readonly {
          readonly Name: string;
          readonly Value: string;
        }[]
      | null;
  } | null;
}

/**
 * Extracts the display name from a user with Cognito attributes.
 * Falls back through: firstName+lastName > firstName > lastName > title > "Unknown User"
 * @param user - User object with optional Cognito attributes
 * @returns Display name string
 */
export const getUserDisplayName = (
  user: ActivityUser | null | undefined
): string => {
  if (!user) return "Unknown User";

  const cognitoAttributes =
    user.cognitoUser?.Attributes ?? user.cognitoUser?.UserAttributes;

  const firstName = cognitoAttributes?.find(
    attr => attr.Name === "given_name"
  )?.Value;
  const lastName = cognitoAttributes?.find(
    attr => attr.Name === "family_name"
  )?.Value;

  return firstName && lastName
    ? `${firstName} ${lastName}`
    : firstName || lastName || user.title || "Unknown User";
};
